import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { api } from "../../helpers/utils";

const getReportsFromResponse = (response) => {
  const payload = response?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.reports)) return payload.reports;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.reports)) return payload.data.reports;
  if (Array.isArray(payload?.result)) return payload.result;

  return [];
};

const getReportId = (report) =>
  report.reportId || report._id || report.id;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const formatDate = (value) => {
  if (!value) return "Not available";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function AdminReports() {
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [replies, setReplies] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [submittingId, setSubmittingId] = useState(null);
  const [feedback, setFeedback] = useState({});

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setFetchError("");

    try {
      const response = await api.post("/reports/get-reports");
      setReports(getReportsFromResponse(response));
    } catch (error) {
      console.error("Error fetching reports:", error);
      setFetchError(getErrorMessage(error, "Unable to fetch reports."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const submitReply = async (event, reportId) => {
    event.preventDefault();
    const reply = replies[reportId]?.trim();

    if (!reply) {
      setFeedback((current) => ({
        ...current,
        [reportId]: { type: "error", message: "Enter a reply before sending." },
      }));
      return;
    }

    setSubmittingId(reportId);
    setFeedback((current) => ({ ...current, [reportId]: null }));

    try {
      const response = await api.post("/reports/admin-reply", {
        reportId,
        reply,
      });

      const status = response.data?.status;
      const isFailedStatus =
        status === false ||
        (typeof status === "number" && status !== 100 && status !== 200);

      if (isFailedStatus) {
        throw new Error(response.data.message || "Unable to send reply.");
      }

      setReplies((current) => ({ ...current, [reportId]: "" }));
      setFeedback((current) => ({
        ...current,
        [reportId]: { type: "success", message: "Reply sent successfully." },
      }));
      await fetchReports();
    } catch (error) {
      console.error("Error replying to report:", error);
      setFeedback((current) => ({
        ...current,
        [reportId]: {
          type: "error",
          message: getErrorMessage(error, "Unable to send reply."),
        },
      }));
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <Page>
      <Header>
        <BackButton type="button" onClick={() => nav("/admin/home")}>
          ← Back
        </BackButton>
        <TitleGroup>
          <Title>Admin Reports</Title>
          <Subtitle>Review user reports and send admin replies</Subtitle>
        </TitleGroup>
        <RefreshButton type="button" onClick={fetchReports} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </RefreshButton>
      </Header>

      {loading ? (
        <StateMessage>Loading reports...</StateMessage>
      ) : fetchError ? (
        <ErrorState>
          <span>{fetchError}</span>
          <RetryButton type="button" onClick={fetchReports}>Try again</RetryButton>
        </ErrorState>
      ) : reports.length === 0 ? (
        <StateMessage>No reports found.</StateMessage>
      ) : (
        <ReportList>
          {reports.map((report, index) => {
            const reportId = getReportId(report);
            const message =
              report.reportMessage ||
              report.report ||
              report.reason ||
              report.message ||
              report.description ||
              "No report message provided.";
            const existingReply =
              report.adminReply || report.reply || report.admin_reply;

            return (
              <ReportCard key={reportId || index}>
                <CardHeader>
                  <ReportNumber>Report #{index + 1}</ReportNumber>
                  <Status>{report.status || (existingReply ? "replied" : "pending")}</Status>
                </CardHeader>

                <Details>
                  <Detail>
                    <DetailLabel>Report ID</DetailLabel>
                    <DetailValue>{reportId || "Not available"}</DetailValue>
                  </Detail>
                  <Detail>
                    <DetailLabel>Reported by</DetailLabel>
                    <DetailValue>
                      {report.reporterLabel ||
                        report.reportedByName ||
                        report.reporterName ||
                        report.userName ||
                        report.reportedBy ||
                        report.userId ||
                        "Not available"}
                    </DetailValue>
                  </Detail>
                  <Detail>
                    <DetailLabel>Created</DetailLabel>
                    <DetailValue>
                      {formatDate(report.createdAt || report.timestamp || report.reportedAt)}
                    </DetailValue>
                  </Detail>
                  <Detail>
                    <DetailLabel>User ID</DetailLabel>
                    <DetailValue>{report.userId || "Not available"}</DetailValue>
                  </Detail>
                  <Detail>
                    <DetailLabel>Report type</DetailLabel>
                    <DetailValue>
                      {report.reportType || report.type || "Not available"}
                    </DetailValue>
                  </Detail>
                  <Detail>
                    <DetailLabel>Expires</DetailLabel>
                    <DetailValue>{formatDate(report.expireAt)}</DetailValue>
                  </Detail>
                </Details>

                <MessageBlock>
                  <DetailLabel>Report</DetailLabel>
                  <Message>{message}</Message>
                </MessageBlock>

                {existingReply ? (
                  <ExistingReply>
                    <DetailLabel>Current admin reply</DetailLabel>
                    <Message>{existingReply}</Message>
                  </ExistingReply>
                ) : null}

                <ReplyForm onSubmit={(event) => submitReply(event, reportId)}>
                  <ReplyInput
                    value={replies[reportId] || ""}
                    onChange={(event) =>
                      setReplies((current) => ({
                        ...current,
                        [reportId]: event.target.value,
                      }))
                    }
                    placeholder="Write an admin reply..."
                    rows="4"
                    disabled={!reportId || submittingId === reportId}
                  />
                  {feedback[reportId] ? (
                    <Feedback type={feedback[reportId].type}>
                      {feedback[reportId].message}
                    </Feedback>
                  ) : null}
                  <SendButton
                    type="submit"
                    disabled={!reportId || submittingId === reportId}
                  >
                    {submittingId === reportId ? "Sending..." : "Send reply"}
                  </SendButton>
                </ReplyForm>
              </ReportCard>
            );
          })}
        </ReportList>
      )}
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  background: #000;
  color: #fff;
  padding: 40px 20px 80px;
`;

const Header = styled.header`
  width: min(1000px, 100%);
  margin: 0 auto 30px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 20px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr auto;
  }
`;

const TitleGroup = styled.div`
  text-align: center;

  @media (max-width: 680px) {
    grid-column: 1 / -1;
    grid-row: 1;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 36px;

  @media (max-width: 560px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  margin: 8px 0 0;
  color: #bdbdbd;
  font-size: 15px;
`;

const Button = styled.button`
  padding: 10px 16px;
  border: 1px solid #fff;
  background: #000;
  color: #fff;
  font-weight: 800;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #fff;
    color: #000;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const BackButton = styled(Button)``;
const RefreshButton = styled(Button)``;
const RetryButton = styled(Button)``;

const StateMessage = styled.div`
  width: min(1000px, 100%);
  margin: 0 auto;
  padding: 70px 20px;
  border: 1px solid #303030;
  background: #050505;
  color: #bdbdbd;
  text-align: center;
`;

const ErrorState = styled(StateMessage)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

const ReportList = styled.main`
  width: min(1000px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 20px;
`;

const ReportCard = styled.article`
  padding: 24px;
  border: 1px solid #303030;
  background: #050505;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const ReportNumber = styled.h2`
  margin: 0;
  font-size: 21px;
`;

const Status = styled.span`
  padding: 6px 10px;
  border: 1px solid #555;
  color: #ddd;
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Detail = styled.div`
  min-width: 0;
  padding: 14px;
  background: #000;
  border: 1px solid #252525;
`;

const DetailLabel = styled.div`
  margin-bottom: 7px;
  color: #8c8c8c;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`;

const DetailValue = styled.div`
  color: #fff;
  font-size: 14px;
  overflow-wrap: anywhere;
`;

const MessageBlock = styled.div`
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #303030;
  background: #000;
`;

const ExistingReply = styled(MessageBlock)`
  border-color: #4a4a4a;
`;

const Message = styled.p`
  margin: 0;
  color: #eee;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const ReplyForm = styled.form`
  display: grid;
  gap: 12px;
  margin-top: 20px;
`;

const ReplyInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  padding: 14px;
  border: 1px solid #404040;
  background: #000;
  color: #fff;
  font: inherit;
  outline: none;

  &:focus {
    border-color: #fff;
  }
`;

const Feedback = styled.p`
  margin: 0;
  color: ${({ type }) => (type === "success" ? "#86efac" : "#fca5a5")};
  font-size: 13px;
`;

const SendButton = styled(Button)`
  justify-self: end;
  min-width: 140px;
  background: #fff;
  color: #000;

  &:hover:not(:disabled) {
    background: #000;
    color: #fff;
  }
`;
